import React, { useLayoutEffect, useRef, useState } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  Cell,
  ResponsiveContainer,
} from "recharts";

// Default tooltip, styled to match the org-admin Dashboard's bar chart
// (see StackedBarChart.jsx) — used whenever the caller doesn't pass renderTooltip.
const DefaultTooltip = ({ active, payload, label, xKey, yKey }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        borderRadius: "4px",
        border: "1px solid #E5E7EB",
        padding: "8px 12px",
      }}
    >
      <p style={{ fontSize: "13px", fontWeight: 700, marginBottom: 4, color: "#707070" }}>
        {label}
      </p>
      <p style={{ fontSize: "12px", color: "#374151", padding: "2px 0" }}>
        {yKey}: {payload[0]?.payload?.[yKey]}
      </p>
    </div>
  );
};

const BarChart = ({
  data = [],
  xKey,
  yKey,
  isDark = false,
  yDomain,
  yLabel,
  colorForEntry = () => "#4D70EE",
  barRadius = [4, 4, 0, 0],
  barSize = 28,
  height = 350,
  minBarWidth = 60,
  minWidth = 800,
  xTickAngle = -45,
  xTickHeight = 36,
  onBarClick,
  renderTooltip,
  showValueLabels = true,
}) => {
  const dynamicWidth = Math.max(minWidth, data.length * minBarWidth);
  const axisTickColor = isDark ? "#AEB9E1" : "#707070";
  const gridStroke = isDark ? "#34353CA3" : "#E0E0E0";

  // When asked to fill the parent ("100%"), measure the container's real pixel
  // height ourselves rather than handing recharts a CSS percentage — its
  // ResponsiveContainer doesn't reliably re-measure through a horizontally
  // scrolling ancestor, which left the chart stuck at a stale (smaller) height.
  const fillParent = height === "100%";
  const containerRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  useLayoutEffect(() => {
    if (!fillParent || !containerRef.current) return undefined;
    const el = containerRef.current;
    const updateHeight = () => setMeasuredHeight(el.clientHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [fillParent]);

  const resolvedHeight = fillParent ? measuredHeight || "100%" : height;

  return (
    <div ref={containerRef} className="overflow-x-auto h-full">
      <div style={{ width: `${dynamicWidth}px`, minWidth: "100%" }}>
        <ResponsiveContainer width="100%" height={resolvedHeight}>
          <RechartsBarChart
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: xTickHeight }}
          >
            <CartesianGrid strokeDasharray="4 4" stroke={gridStroke} />
            <XAxis
              dataKey={xKey}
              tick={{ fontSize: 11, fill: axisTickColor }}
              interval={0}
              height={xTickHeight}
              angle={xTickAngle}
              textAnchor="end"
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={yDomain}
              tick={{ fill: axisTickColor, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              label={
                yLabel
                  ? {
                      value: yLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle", fill: axisTickColor },
                    }
                  : undefined
              }
            />
            <Tooltip
              cursor={{ fill: "rgba(0, 0, 0, 0.1)" }}
              content={
                renderTooltip
                  ? ({ active, payload }) =>
                      active && payload && payload.length
                        ? renderTooltip(payload[0].payload)
                        : null
                  : (props) => <DefaultTooltip {...props} xKey={xKey} yKey={yKey} />
              }
            />
            <Bar dataKey={yKey} radius={barRadius} barSize={barSize} onClick={onBarClick} cursor="pointer">
              {showValueLabels && (
                <LabelList
                  dataKey={yKey}
                  position="top"
                  style={{ fontSize: "11px", fontWeight: "bold", fill: axisTickColor }}
                />
              )}
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colorForEntry(entry)} />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChart;
